/**
 * Routes of area (a) — creation. OWNED BY area (a) — creation. Paths are kept (boards.ts hrefs point at them);
 * search params are validated with Zod; the dossier route picks its board from data (state → drawer step,
 * `?fenetre=` → window).
 */
import { lazy, Suspense, type ReactNode } from "react";
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { guard, staffRoute } from "@/app/route-base";
import { PageSkeleton } from "@/ui/page";
import { BoTab } from "@/contracts/areas/creation";
import { FENETRES } from "./shared";

const S = ({ children }: { children: ReactNode }) => <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;

const Screens = {
  List: lazy(() => import("./screens").then((m) => ({ default: m.ListScreen }))),
  Dossier: lazy(() => import("./screens").then((m) => ({ default: m.DossierScreen }))),
  MixClient: lazy(() => import("./mix-client").then((m) => ({ default: m.MixClientScreen }))),
  Etats: lazy(() => import("./flows").then((m) => ({ default: m.FlowsEtats }))),
  Invalidations: lazy(() => import("./flows").then((m) => ({ default: m.FlowsInvalidations }))),
};

const ListSearch = z.object({
  type: z.enum(["auto-entrepreneur", "societes"]).optional().catch(undefined),
  /** « Demandes en attente » sub-filter: « À traiter » / « En attente du client ». */
  groupe: z.enum(["a-traiter", "client"]).optional().catch(undefined),
});
const listSearch = (s: Record<string, unknown>) => ListSearch.parse(s);

const opt = z.string().max(80).optional().catch(undefined);
const DossierSearch = z.object({
  fenetre: z.enum(FENETRES as unknown as [string, ...string[]]).optional().catch(undefined),
  champ: opt,
  piece: opt,
  doc: opt,
  point: opt,
  kind: opt,
  onglet: BoTab.optional().catch(undefined),
  etape: z.string().regex(/^\d{1,2}(,\d{1,2})*$/).optional().catch(undefined),
  montant: z.string().regex(/^\d{1,6}([,.]\d{1,2})?$/).optional().catch(undefined),
  motif: z.string().max(500).optional().catch(undefined),
});
const dossierSearch = (s: Record<string, unknown>) => DossierSearch.parse(s);

/** BO-Creation-Nouvelles */
export const creationNouvellesRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/nouvelles",
  validateSearch: listSearch,
  beforeLoad: guard("creation", "read"),
  component: () => <S><Screens.List tab="nouvelles" /></S>,
});

/** BO-Creation-Attente, BO-Creation-Attente-AE (?type=auto-entrepreneur) */
export const creationEnAttenteRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/en-attente",
  validateSearch: listSearch,
  beforeLoad: guard("creation", "read"),
  component: () => <S><Screens.List tab="en-attente" /></S>,
});

/** BO-Creation-AttenteClient */
export const creationAttenteClientRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/attente-client",
  validateSearch: listSearch,
  beforeLoad: guard("creation", "read"),
  component: () => <S><Screens.List tab="attente-client" /></S>,
});

/** BO-Creation-Terminees */
export const creationTermineesRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/terminees",
  validateSearch: listSearch,
  beforeLoad: guard("creation", "read"),
  component: () => <S><Screens.List tab="terminees" /></S>,
});

/** Every BO-Tiroir-* / window board: the dossier's state picks the drawer step, `?fenetre=` the window. */
export const creationDossiersDossieridRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/dossiers/$dossierId",
  validateSearch: dossierSearch,
  beforeLoad: guard("creation", "read"),
  component: () => <S><Screens.Dossier /></S>,
});

/** BO-Mix-Client */
export const comptaClientsCompanyidCompteRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/clients/$companyId/compte",
  validateSearch: (s: Record<string, unknown>) => z.object({ fenetre: z.enum(["autre-societe"]).optional().catch(undefined) }).parse(s),
  beforeLoad: guard("cabinet.clients", "read"),
  component: () => <S><Screens.MixClient /></S>,
});

/** Flows-Etats */
export const referenceCreationEtatsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/creation/etats",
  validateSearch: (s: Record<string, unknown>) => z.object({}).parse(s),
  beforeLoad: guard("reference", "read"),
  component: () => <S><Screens.Etats /></S>,
});

/** Flows-Invalidations */
export const referenceCreationInvalidationsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/creation/invalidations",
  validateSearch: (s: Record<string, unknown>) => z.object({}).parse(s),
  beforeLoad: guard("reference", "read"),
  component: () => <S><Screens.Invalidations /></S>,
});

export const creationRoutes = [
  creationNouvellesRoute,
  creationEnAttenteRoute,
  creationAttenteClientRoute,
  creationTermineesRoute,
  creationDossiersDossieridRoute,
  comptaClientsCompanyidCompteRoute,
  referenceCreationEtatsRoute,
  referenceCreationInvalidationsRoute,
] as const;
